import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const AdminPricing = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/pricing/config").then(({ data }) => setCfg(data)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/pricing/config", cfg); toast.success("Pricing updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading || !cfg) return <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;

  return (
    <div className="p-6 lg:p-10 max-w-3xl pav-fade-in" data-testid="admin-pricing">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Configuration</p>
        <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">Pricing config</h1>
        <p className="text-neutral-500 text-sm mt-1">Adjust tarpaulin base rate, finishing fees, and delivery cost.</p>
      </div>

      <div className="pav-card space-y-5">
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 block mb-1.5">Base price per sq.ft (₱)</label>
          <input type="number" min="0" step="0.5" className="pav-input"
            data-testid="config-base-price-input"
            value={cfg.base_price_per_sqft}
            onChange={(e) => setCfg({ ...cfg, base_price_per_sqft: parseFloat(e.target.value) || 0 })} />
        </div>
        <div>
          <h3 className="font-display font-bold mb-3">Finishing fees (₱ per piece)</h3>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(cfg.finishing_fees).map(([k, v]) => (
              <div key={k}>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 block mb-1.5">{k.replace(/_/g, " ")}</label>
                <input type="number" min="0" step="1" className="pav-input"
                  data-testid={`config-finishing-${k}-input`}
                  value={v}
                  onChange={(e) => setCfg({ ...cfg, finishing_fees: { ...cfg.finishing_fees, [k]: parseFloat(e.target.value) || 0 } })} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-display font-bold mb-3">Resolution multipliers</h3>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(cfg.resolution_multipliers).map(([k, v]) => (
              <div key={k}>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 block mb-1.5">{k.replace(/_/g, " ")}</label>
                <input type="number" min="0.5" step="0.05" className="pav-input"
                  data-testid={`config-res-${k}-input`}
                  value={v}
                  onChange={(e) => setCfg({ ...cfg, resolution_multipliers: { ...cfg.resolution_multipliers, [k]: parseFloat(e.target.value) || 1 } })} />
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 block mb-1.5">Delivery flat fee (₱)</label>
            <input type="number" min="0" step="10" className="pav-input"
              data-testid="config-delivery-flat-input"
              value={cfg.delivery_flat_fee}
              onChange={(e) => setCfg({ ...cfg, delivery_flat_fee: parseFloat(e.target.value) || 0 })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 block mb-1.5">Delivery per km (₱)</label>
            <input type="number" min="0" step="0.5" className="pav-input"
              data-testid="config-delivery-perkm-input"
              value={cfg.delivery_per_km}
              onChange={(e) => setCfg({ ...cfg, delivery_per_km: parseFloat(e.target.value) || 0 })} />
          </div>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="save-pricing-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </button>
      </div>
    </div>
  );
};

export default AdminPricing;
